import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { PasswordResetLoginService } from '../../../../platform/services/password-reset-login.service';
import { resetPasswordAction, resetPasswordErrorAction } from '../../../actions/login-prefill.actions';
import { closeDialogWithAnimation } from '../../../dialogs-utils';
import { PasswordResetDialogCloseReason } from '../../../dialogs/enums/PasswordResetDialogCloseReason.enum';
import { ErrorModel } from '../../../models/api/error.model';
import { AppStateModel } from '../../../models/auxiliary/app-state.model';
import { selectLoginPrefill } from '../../../selectors/login-prefill.selector';
import { TurnstileService } from '../../../services/turnstile.service';
import { PASSWORD_MIN_LENGTH, passwordValidator } from '../../../validators/password.validator';
import { AbstractComponent } from '../../abstract.component';
import { InputLogoComponent } from '../../input-logo/input-logo.component';
import { LandingDialogButtonComponent } from '../../landing-button/landing-dialog-button.component';
import { LandingDialogTitleComponent } from '../landing-dialog-title/landing-dialog-title.component';
import { LandingDialogComponent } from '../landing-dialog/landing-dialog.component';

@Component({
  selector: 'bp-password-reset-dialog',
  templateUrl: './password-reset-dialog.component.html',
  styleUrls: ['./password-reset-dialog.component.scss'],
  standalone: true,
  imports: [
    LandingDialogComponent,
    LandingDialogTitleComponent,
    FormsModule,
    ReactiveFormsModule,
    InputLogoComponent,
    NgFor,
    NgIf,
    LandingDialogButtonComponent,
  ],
})
export class PasswordResetDialogComponent extends AbstractComponent implements AfterViewInit, OnDestroy {
  recoveryForm: UntypedFormGroup;
  apiErrors: string[] = [];
  hash: string;
  submitButtonDisabled: boolean;
  tooltipPosition: 'landing-bottom' | 'right' = 'right';
  turnstileToken: string | null = null;
  turnstileWidgetId: string | undefined;
  readonly PasswordResetDialogCloseReason = PasswordResetDialogCloseReason;

  readonly errorMap = new Map<string, string>([
    ['required', 'Password required'],
    ['password', 'Enter a valid password'],
  ]);
  readonly requirementMap = new Map<string, string>([
    ['passLength', `at least ${PASSWORD_MIN_LENGTH} characters`],
    ['passUpper', 'contains uppercase letters'],
    ['passLower', 'contains lowercase letters'],
    ['passDigits', 'contains digits'],
  ]);

  private readonly store = inject(Store<AppStateModel>);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly turnstileService = inject(TurnstileService);
  private readonly elementRef = inject(ElementRef);
  private readonly passwordResetDialogRef = inject(MatDialogRef<PasswordResetDialogComponent>);
  private readonly passwordResetLoginService = inject(PasswordResetLoginService);
  private readonly actions = inject(Actions);

  constructor() {
    super();

    this.setTooltipPositioning();

    this.hash = this.passwordResetLoginService.getRecoveryHash();
    this.recoveryForm = this.fb.group({
      password: ['', [Validators.required, passwordValidator]],
    });

    this.store
      .select(selectLoginPrefill)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        if (state.errors !== null) {
          this.apiErrors = state.errors.map((error: ErrorModel) => error.message);
          this.submitButtonDisabled = false;
          return;
        }

        if (state.data) {
          this.apiErrors = [];
          this.closeDialog(PasswordResetDialogCloseReason.LOGIN);
        }
      });

    this.actions.pipe(ofType(resetPasswordErrorAction), takeUntil(this.unsubscribe$)).subscribe(() => {
      if (window.turnstile && this.turnstileWidgetId) {
        window.turnstile.remove(this.turnstileWidgetId);
        this.turnstileToken = null;
        this.initTurnstile();
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.turnstileToken) {
      this.initTurnstile();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.turnstileWidgetId && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId);
    }
  }

  @HostListener('window:resize', ['$event'])
  setTooltipPositioning(): void {
    if (window.innerWidth < 860) {
      this.tooltipPosition = 'landing-bottom';
    } else {
      this.tooltipPosition = 'right';
    }
  }

  get passwordErrors(): { [errorKey: string]: boolean } {
    return this.recoveryForm.get('password').errors;
  }

  setPassword(): void {
    this.submitButtonDisabled = true;
    this.store.dispatch(
      resetPasswordAction({
        hash: this.hash,
        body: this.recoveryForm.value,
        turnstileToken: this.turnstileToken,
      }),
    );
    if (!this.apiErrors) {
      this.closeDialog(PasswordResetDialogCloseReason.LOGIN);
    }
    this.passwordResetLoginService.setRecoveryHash(null);
  }

  closeDialog(reason: PasswordResetDialogCloseReason): void {
    closeDialogWithAnimation(this.passwordResetDialogRef, reason);
  }

  initTurnstile(): void {
    this.turnstileService
      .initializeTurnstileScriptAndWidget(this.elementRef, this.unsubscribe$)
      .then(({ turnstileWidgetId, turnstileToken }) => {
        this.turnstileWidgetId = turnstileWidgetId;
        this.turnstileToken = turnstileToken;
      });
  }
}
